import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { mdiDownload } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { $api } from '../api/queryClient'
import { apiClient } from '../api/client'
import type { RentRoll, RentRollRow } from '../api/schema'
import { useFormatters } from '../lib/format'
import { LeaseVerdictBadge, useLeaseRegimes } from './LeaseVerdictBadge'
import { RentRollCompare } from './RentRollReconciliation'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { EmptyState } from './ui/empty-state'
import { AppIcon } from './ui/icon'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Skeleton } from './ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { SECTION_ICONS } from '../lib/section-icons'

const today = () => new Date().toISOString().slice(0, 10)

const ORGANIZATIONAL_TYPES = ['portfolio', 'agency', 'entity']

/**
 * The rent roll of a building, a site or an organizational asset (spec
 * 2026-10-25-03 §4, 2026-10-27-15): every lettable lot, let or vacant, the
 * rent in force of each running lease with its indexation and key dates,
 * totals per currency and, over several buildings, a building column and a
 * subtotal row per building. Downloads as CSV; compares with an ERP rent roll.
 */
export function AssetRentRollTab({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('leases')
    const { formatDate, formatMoney } = useFormatters()
    const [at, setAt] = useState(today())
    const { regimeLabel } = useLeaseRegimes(org)
    const [downloading, setDownloading] = useState(false)

    const { data: raw, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/leases/rent-roll', {
        params: { path: { orgCode: org }, query: { asset_uid: assetUid, at } },
    })
    // The route also answers text/csv; the JSON form is the one asked here.
    const data = typeof raw === 'object' ? (raw as RentRoll) : undefined

    const download = async () => {
        setDownloading(true)
        try {
            const { data: blob, response } = await apiClient.GET('/api/v1/organizations/{orgCode}/leases/rent-roll', {
                params: { path: { orgCode: org }, query: { asset_uid: assetUid, at, format: 'csv' } },
                parseAs: 'blob',
            })
            if (!blob) return
            const disposition = response.headers.get('Content-Disposition') ?? ''
            const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `rent-roll-${at}.csv`
            const url = URL.createObjectURL(blob as Blob)
            const a = document.createElement('a')
            a.href = url
            a.download = name
            a.click()
            URL.revokeObjectURL(url)
        } finally {
            setDownloading(false)
        }
    }

    const date = (d?: string | null) => (d ? formatDate(d) : '—')
    const perBuilding = !!data && (ORGANIZATIONAL_TYPES.includes(data.asset_type_code) || (data.buildings ?? []).length > 1)
    const columnCount = perBuilding ? 17 : 16
    const buildingKey = (row: RentRollRow) => row.building?.uid ?? ''
    const subtotal = (key: string) => {
        const b = (data?.buildings ?? []).find((x) => (x.building.uid ?? '') === key)
        if (!b) return null
        return (
            <TableRow key={`subtotal-${key}`} className="bg-muted/40 font-medium" data-testid="rent-roll-building-subtotal">
                <TableCell colSpan={columnCount}>
                    {t('rent_roll.subtotal', { building: b.building.name || '—', lettable: b.lettable_lots, vacant: b.vacant_lots, leases: b.lease_count })}
                    {b.totals.map((tot) => (
                        <span key={tot.currency} className="ml-3 tabular-nums">
                            {t('rent_roll.total_line', { count: tot.lease_count, amount: formatMoney(tot.annual_rent_minor, tot.currency) })}
                        </span>
                    ))}
                </TableCell>
            </TableRow>
        )
    }

    return (
        <div className="space-y-4" data-testid="rent-roll-tab">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <Label className="mb-1 block text-sm" htmlFor="rent-roll-at">{t('rent_roll.at')}</Label>
                    <Input id="rent-roll-at" type="date" value={at} onChange={(e) => setAt(e.target.value || today())} className="w-44" data-testid="rent-roll-at" />
                </div>
                <Button variant="outline" size="sm" onClick={() => void download()} disabled={downloading || !data} data-testid="rent-roll-download-csv">
                    <AppIcon path={mdiDownload} size="sm" className="mr-1" />
                    {t('rent_roll.download_csv')}
                </Button>
            </div>

            {isLoading ? (
                <Skeleton className="h-40 w-full" />
            ) : !data || data.rows.length === 0 ? (
                <EmptyState icon={SECTION_ICONS.leases} title={t('rent_roll.empty')} data-testid="rent-roll-empty" />
            ) : (
                <>
                    <p className="text-sm text-muted-foreground" data-testid="rent-roll-summary">
                        {t('rent_roll.summary', { lettable: data.lettable_lots, vacant: data.vacant_lots, leases: data.lease_count })}
                    </p>
                    <Card>
                        <CardContent className="overflow-x-auto p-0">
                            <Table data-testid="rent-roll-table">
                                <TableHeader>
                                    <TableRow>
                                        {perBuilding && <TableHead>{t('rent_roll.column.building')}</TableHead>}
                                        <TableHead>{t('rent_roll.column.lots')}</TableHead>
                                        <TableHead>{t('rent_roll.column.floor')}</TableHead>
                                        <TableHead>{t('rent_roll.column.tenant')}</TableHead>
                                        <TableHead>{t('rent_roll.column.regime')}</TableHead>
                                        <TableHead className="text-right">{t('rent_roll.column.rent')}</TableHead>
                                        <TableHead>{t('rent_roll.column.frequency')}</TableHead>
                                        <TableHead className="text-right">{t('rent_roll.column.annual_rent')}</TableHead>
                                        <TableHead className="text-right">{t('rent_roll.column.charges')}</TableHead>
                                        <TableHead>{t('rent_roll.column.start')}</TableHead>
                                        <TableHead>{t('rent_roll.column.term')}</TableHead>
                                        <TableHead>{t('rent_roll.column.verdict')}</TableHead>
                                        <TableHead className="text-right">{t('rent_roll.column.rent_per_area')}</TableHead>
                                        <TableHead>{t('rent_roll.column.indexation')}</TableHead>
                                        <TableHead>{t('rent_roll.column.next_break')}</TableHead>
                                        <TableHead>{t('rent_roll.column.status')}</TableHead>
                                        <TableHead>{t('rent_roll.column.flags')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.rows.flatMap((row, i) => {
                                        const last = perBuilding && (i === data.rows.length - 1 || buildingKey(data.rows[i + 1]) !== buildingKey(row))
                                        const buildingCell = perBuilding ? (
                                            <TableCell className="min-w-32" data-testid="rent-roll-row-building">{row.building?.name || '—'}</TableCell>
                                        ) : null
                                        const line = row.vacant ? (
                                            <TableRow key={`vacant-${row.lots[0]?.uid ?? i}`} data-testid="rent-roll-row-vacant">
                                                {buildingCell}
                                                <TableCell className="min-w-40 font-medium">
                                                    <Link to="/$org/assets/$assetUid" params={{ org, assetUid: row.lots[0].uid }} className="hover:underline">
                                                        {row.lots[0].name}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{row.floor || '—'}</TableCell>
                                                <TableCell colSpan={14}>
                                                    <Badge variant="secondary">{t('rent_roll.vacant')}</Badge>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <TableRow key={row.lease_uid ?? i} data-testid="rent-roll-row" data-status={row.status}>
                                                {buildingCell}
                                                <TableCell className="min-w-40 font-medium">
                                                    <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: row.lease_uid ?? '' }} className="hover:underline">
                                                        {row.lots.map((l) => l.name).join(' + ')}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{row.floor || '—'}</TableCell>
                                                <TableCell>{row.tenants.join(', ') || '—'}</TableCell>
                                                <TableCell>{regimeLabel(row.regime_code)}</TableCell>
                                                <TableCell className="text-right tabular-nums">{formatMoney(row.rent_amount_minor, row.currency)}</TableCell>
                                                <TableCell>{row.rent_frequency ? t(`frequency_name.${row.rent_frequency}`, { defaultValue: row.rent_frequency }) : '—'}</TableCell>
                                                <TableCell className="text-right tabular-nums" data-testid="rent-roll-row-annual-rent">
                                                    {formatMoney(row.annual_rent_minor, row.currency)}
                                                    {row.rent_source === 'term' && row.rent_term_effective_from && (
                                                        <div className="text-xs text-muted-foreground" data-testid="rent-roll-row-rent-term">
                                                            {t('rent_roll.rent_term_from', { date: date(row.rent_term_effective_from) })}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">{formatMoney(row.charges_amount_minor, row.currency)}</TableCell>
                                                <TableCell>{date(row.start_date)}</TableCell>
                                                <TableCell>{row.term ? date(row.term) : t('open_ended')}</TableCell>
                                                <TableCell>
                                                    <LeaseVerdictBadge verdict={row.verdict} />
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums" data-testid="rent-roll-row-rent-per-area">
                                                    {row.rent_per_area != null ? formatMoney(row.rent_per_area, row.currency) : '—'}
                                                </TableCell>
                                                <TableCell className="min-w-32 text-sm" data-testid="rent-roll-row-indexation">
                                                    {row.index_code ? (
                                                        <>
                                                            <div>{[row.index_code, row.index_reference].filter(Boolean).join(' · ')}</div>
                                                            {row.next_review_date && (
                                                                <div className="text-xs text-muted-foreground">{t('rent_roll.next_review', { date: date(row.next_review_date) })}</div>
                                                            )}
                                                        </>
                                                    ) : row.review_terms_approved === false ? (
                                                        <span className="text-muted-foreground">{t('rent_roll.review_terms_unapproved')}</span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                    {row.last_review_date && (
                                                        <div className="text-xs text-muted-foreground">{t('rent_roll.last_review', { date: date(row.last_review_date) })}</div>
                                                    )}
                                                </TableCell>
                                                <TableCell data-testid="rent-roll-row-next-break">
                                                    {row.next_break ? `${date(row.next_break.date)} (${t(`rent_roll.party.${row.next_break.party}`)})` : '—'}
                                                    {row.next_break_notice_deadline && (
                                                        <div className="text-xs text-muted-foreground" data-testid="rent-roll-row-notice-deadline">
                                                            {t('rent_roll.notice_deadline', { date: date(row.next_break_notice_deadline) })}
                                                        </div>
                                                    )}
                                                    {row.firm_term_end && (
                                                        <div className="text-xs text-muted-foreground">{t('rent_roll.firm_term_end', { date: date(row.firm_term_end) })}</div>
                                                    )}
                                                </TableCell>
                                                <TableCell data-testid="rent-roll-row-status">
                                                    {row.status === 'to_regularize' ? (
                                                        <Badge variant="warning">{t('rent_roll.status.to_regularize')}</Badge>
                                                    ) : (
                                                        t('rent_roll.status.in_force')
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {(row.flags ?? []).map((f) => (
                                                            <Badge key={f} variant="warning" data-testid={`rent-roll-row-flag-${f}`}>
                                                                {t(`rent_roll.flag.${f}`)}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )
                                        return last ? [line, subtotal(buildingKey(row))] : [line]
                                    })}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                    <div className="space-y-1 text-sm" data-testid="rent-roll-totals">
                        <div className="font-medium">{t('rent_roll.totals')}</div>
                        {data.totals.map((tot) => (
                            <div key={tot.currency} data-testid={`rent-roll-total-${tot.currency || 'none'}`}>
                                {t('rent_roll.total_line', { count: tot.lease_count, amount: formatMoney(tot.annual_rent_minor, tot.currency) })}
                            </div>
                        ))}
                        {data.totals.some((tot) => tot.walt_years != null) && (
                            <div className="text-muted-foreground" data-testid="rent-roll-walt">
                                {data.totals
                                    .filter((tot) => tot.walt_years != null)
                                    .map((tot) => (
                                        <div key={tot.currency} data-testid={`rent-roll-walt-${tot.currency || 'none'}`}>
                                            {t('rent_roll.walt', { years: (tot.walt_years ?? 0).toFixed(1), currency: tot.currency })}
                                        </div>
                                    ))}
                            </div>
                        )}
                        {data.totals.map((tot) =>
                            tot.to_regularize_count > 0 ? (
                                <div key={tot.currency} className="text-muted-foreground" data-testid="rent-roll-to-regularize-subtotal">
                                    {t('rent_roll.to_regularize_subtotal', { count: tot.to_regularize_count, amount: formatMoney(tot.to_regularize_annual_rent_minor, tot.currency) })}
                                </div>
                            ) : null,
                        )}
                        {data.rent_unknown_count > 0 && (
                            <div className="text-muted-foreground" data-testid="rent-roll-rent-unknown">
                                {t('rent_roll.rent_unknown', { count: data.rent_unknown_count })}
                            </div>
                        )}
                    </div>
                </>
            )}
            <RentRollCompare org={org} assetUid={assetUid} />
        </div>
    )
}
